'use client'

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { auth } from '@/firebase/config'
import { type User, onAuthStateChanged } from 'firebase/auth'
import { clearAuthSessionCookie, clearLegacyAlumniIdTokenCookie, setAuthSessionCookie } from '@/lib/auth-session'
import { authLogin as doLogin, authLogout as doLogout } from '@/lib/auth-actions'
import { AuthStateContextProvider, type AuthContextValue } from '@/composable/AuthContext'
import { AlumniSessionRouteGuard } from '@/components/AlumniSessionRouteGuard'
import { AlumniStoreBootstrap } from '@/components/AlumniStoreBootstrap'
import { useHasMounted } from '@/hooks/useHasMounted'

/**
 * Un solo `onAuthStateChanged` y un único estado de sesión (alineado a `initAuth` + store en `FrontEnd/certisep`).
 * Mientras `!sessionReady`, overlay a pantalla completa; el resto de rutas sigue bajo el mismo árbol.
 * Se usa en `root.tsx` envolviendo el `<Outlet />` del `Layout` para que toda ruta tenga contexto.
 */
export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
	const mounted = useHasMounted()
	const [user, setUser] = useState<User | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [authToken, setAuthToken] = useState<string | undefined>(undefined)
	const [sessionReady, setSessionReady] = useState(false)
	const [opLoading, setOpLoading] = useState(false)

	useEffect(() => {
		clearLegacyAlumniIdTokenCookie()
	}, [])

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentUser: User | null) => {
			setUser(currentUser)
			setSessionReady(true)
			if (currentUser) {
				setAuthSessionCookie()
				clearLegacyAlumniIdTokenCookie()
			} else {
				clearAuthSessionCookie()
			}
		})
		return () => unsubscribe()
	}, [])

	const login = useCallback(async (email: string, password: string) => {
		setError(null)
		setOpLoading(true)
		try {
			const result = await doLogin(email, password)
			setUser(auth.currentUser)
			return result
		} catch (err) {
			const message = (err as Error).message
			setError(message)
			throw err
		} finally {
			setOpLoading(false)
		}
	}, [])

	const logout = useCallback(async () => {
		setError(null)
		setOpLoading(true)
		try {
			await doLogout()
			setUser(null)
		} catch (err) {
			const message = (err as Error).message
			setError(message)
			throw err
		} finally {
			setOpLoading(false)
		}
	}, [])

	const getAuthToken = useCallback(async () => {
		const token = await auth.currentUser?.getIdToken()
		setAuthToken(token)
		return token
	}, [])

	const loading = !sessionReady || opLoading

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			error,
			loading,
			sessionReady,
			authToken,
			login,
			logout,
			getAuthToken
		}),
		[authToken, error, getAuthToken, loading, login, logout, sessionReady, user]
	)

	return (
		<AuthStateContextProvider value={value}>
			<AlumniStoreBootstrap>
				<AlumniSessionRouteGuard>{mounted ? children : null}</AlumniSessionRouteGuard>
			</AlumniStoreBootstrap>
			{mounted && !sessionReady ? (
				<div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-8 text-zinc-400 backdrop-blur-sm" role="status" aria-live="polite">
					Inicializando sesión…
				</div>
			) : null}
		</AuthStateContextProvider>
	)
}
